import moment, { Moment, MomentFormatSpecification, MomentInput } from "moment";
import i18n from "PFCore/i18n";
import { WeekDay } from "PFTypes";

import { roundToDecimals } from "./rounding";

export const DEFAULT_LOCALE = i18n.language;
export const DEFAULT_DATE_FORMAT = "DD MMM YYYY";

export const ISO_DATE_FORMAT = "YYYY-MM-DD";
export const ISO_YEAR_MONTH_FORMAT = "YYYY-MM";
export const ISO_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";
// The Z on ISO_DATE_TIME_FORMAT makes use of the time zone, making the end date to be the day after
export const ISO_DATE_TIME_FORMAT_NO_OFFSET = "YYYY-MM-DDTHH:mm:ss";
export const ISO_TIME_FORMAT = "HH:mm";

export const TIME_FORMAT_12H = "hh:mm a";
export const TIME_FORMAT_24H = "HH:mm";

export const DATE_MONTH_YEAR = "MMM YYYY";
export const DATE_DAY_MONTH = "DD MMM";

export const utc = (rawDate?: MomentInput, format?: MomentFormatSpecification): Moment =>
  moment.utc(rawDate, format);

export const isDatePassed = (rawDate?: MomentInput): boolean =>
  rawDate ? utc().diff(utc(rawDate).endOf("day")) > 0 : false;

// Expiry dates (e.g. post_until) are inclusive, so they are stored as the end of the selected local day
export const formatEndOfDay = (rawDate?: MomentInput): string => moment(rawDate).endOf("day").format();

export const getWeekdays = (): string[] => moment.weekdays(true);
const getWeekdaysShort = (): string[] => moment.weekdaysShort(true);
export const getWeekdaysMin = (): string[] => getWeekdaysShort().map((day: string) => day[0]);
export const getMonths = (): string[] => moment.months();
export const getWeekdaysStartingAt = (
  day: WeekDay = WeekDay.Monday
): { weekDays: string[]; weekDaysShort: string[] } => {
  const indexOfStartDay = Object.values(WeekDay).indexOf(day);
  const rearrangeWeekdays = (allDays: string[]): string[] => [
    ...allDays.slice(indexOfStartDay),
    ...allDays.slice(0, indexOfStartDay)
  ];

  return {
    weekDays: rearrangeWeekdays(getWeekdays()),
    weekDaysShort: rearrangeWeekdays(getWeekdaysShort())
  };
};

export const fromFormat = (date: string): string =>
  i18n.t("core:time.inWords.fromDuration.current", { from: date });

export const durationFromMonths = (months: number): string => {
  const duration = moment.duration(months, "months");

  if (months > 0) {
    if (months <= 11) {
      return i18n.t("core:time.inWords.months", { count: duration.asMonths() });
    } else {
      if (months % 12 === 0) {
        return i18n.t("core:time.inWords.years", { count: months / 12 });
      } else {
        const wholeYears = Math.floor(months / 12);
        const remainingMonths = months % 12;
        return i18n.t("core:time.inWords.yearsAndMonths", { years: wholeYears, months: remainingMonths });
      }
    }
  } else {
    return "";
  }
};

export const daysFromNowFormat = (date: string): string => {
  const dayDiff = moment(date).diff(moment(), "days");

  return i18n.t("core:time.inWords.days", { count: dayDiff });
};

export const minutesToHours = (minutes = 0): number => roundToDecimals(minutes / 60);
export const hoursToMinutes = (hours = 0): number => roundToDecimals(hours * 60);

export const minutesToReadableDuration = (totalMinutes = 0): string => {
  if (totalMinutes < 60) {
    return i18n.t("core:time.inWords.minutes_short", { count: totalMinutes });
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes
    ? i18n.t("core:time.inWords.duration_short", { hours, minutes })
    : i18n.t("core:time.inWords.hours_short", { count: hours });
};
